import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { dispatch } from 'd3-dispatch';
import { selectAll } from 'd3-selection';
import React, { useEffect, useRef, useState } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { prepareAndPatchTeaserData } from '@/familyTree/index';
import {
  buildFSDateString,
  buildGivenName,
} from '@/familyTree/useFamilySearch/utils';
import {
  usePetCreate,
  usePatchMember,
} from '@/familyTree/useFamilyTree/useFamilyTree';
import {
  ReportFactory,
  reportGetters,
  SearchReportResponseData,
  useReport,
} from '@/reports/index';
import { LoadingDots } from '@/search/index';
import {
  Box,
  useTheme,
  Icons,
  styled,
  IconButton,
  Stack,
  Tooltip,
} from '@/theme/index';
import { ChartWrapper } from './ChartWrapper';
import EditPetModal from './Modals/EditPetModal';
import EditRelationshipModal from './Modals/EditRelationshipModal';
import Tips from './Tips';
import { ChartProps, ZOOM_FACTOR } from './constants';
import { usePrevious } from './utils';
import { JsonIndi } from '../Topola';

const SvgContainer = styled(Box)(() => ({
  flex: '1 1 auto',
  overflow: 'auto',
  position: 'relative',
  height: '100vh',
}));

const PreviewSvgContainer = styled('div')`
  padding: 10px 0 0 0;
  position: relative;
  height: 348px;
  overflow: scroll;
  border: 1px solid #e0e0e0;
`;

const ChartSvg = styled('svg')(() => ({
  margin: 'auto',
  display: 'block',
}));

const StyledStack = styled(Stack)(() => ({
  position: 'sticky',
  left: `10px`,
  top: '10px',
  zIndex: '3',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 0,
  border: `solid ${theme.palette?.progress?.contrastText}`,
  backgroundColor: theme.palette.primary.contrastText,
  height: '60px',
  width: '60px',
}));

const PreviewChartSvg = styled('svg')`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.5);
`;

export function Chart({
  data,
  imgFamilyTreePlaceholder,
  imgFamilyTreeAddRelative,
  imgFamilyTreeDeceased,
  imgFamilyTreeOriginalMember,
  imgFamilySearchIcon,
  imgFamilyTreePetIcon,
  handleNodeClick,
  patchMemberFromDrag,
  softDestroyMemberFromNode,
  isPreview = false,
  selection,
  onSelection,
  sideMenuOpen = false,
  createAncestorReportClick = () => {},
}: ChartProps) {
  const { mutate: postPetMutation } = usePetCreate();
  const [loaded, setLoaded] = useState(false);
  const [editRelationshipModalOpen, setEditRelationshipModalOpen] =
    useState(false);
  const [petEditModalOpen, setPetEditModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<number | null>(null);
  const [selectedPet, setSelectedPet] = useState<number | null>(null);
  const [relativeToRunPermalink, setRelativeToRunPermalink] =
    useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingAncestor, setIsLoadingAncestor] = useState<boolean>(false);
  const [relativeToRunReportOn, setRelativeToRunReportOn] =
    useState<JsonIndi>();

  const chartWrapper = useRef(new ChartWrapper());
  const prevProps = usePrevious({ data, selection });
  const { trackEventGA4, logError, routingUtils } = useAppConfigContext();
  const isInPartialAddressesVariation = useFeatureIsOn('oar-1075');
  const { patchMember } = usePatchMember();
  const { navigate } = routingUtils;
  const { config } = useAppConstantsContext();
  const queryResult = useReport(
    relativeToRunPermalink,
    !relativeToRunPermalink,
    'person'
  );
  const theme = useTheme();

  const softDeleteDispatch = dispatch('softDelete');
  const patchMemberDispatch = dispatch('patchMember');
  const addAdditionalSpouseDispatch = dispatch('addAdditionalSpouse');
  const createAncestorReportDispatch = dispatch('familySearchMoreInfo');
  const openPetModalDispatch = dispatch('openPetModal');
  const handleBVReportOpenDispatch = dispatch('handleBVReportOpen');

  const relativesList = data?.indis || [];

  useEffect(() => {
    addAdditionalSpouseDispatch.on('addAdditionalSpouse', function (id) {
      handleNodeClick({ id }, true);
    });
  }, [addAdditionalSpouseDispatch]);

  useEffect(() => {
    softDeleteDispatch.on('softDelete', function (id) {
      trackEventGA4({
        event_name: 'family_tree_engagement',
        type: 'remove relative',
      });
      softDestroyMemberFromNode(id);
    });
  }, [softDeleteDispatch]);

  useEffect(() => {
    patchMemberDispatch.on('patchMember', function (id) {
      setEditRelationshipModalOpen(true);
      setSelectedNode(id);
    });
  }, [patchMemberDispatch]);

  useEffect(() => {
    createAncestorReportDispatch.on('familySearchMoreInfo', (id) => {
      trackEventGA4({
        event_name: 'family_tree_engagement',
        type: ' ancestor report',
      });

      const relative = relativesList.find(
        (relative) => relative.family_search_id == id
      );

      if (!relative) return;

      createAncestorReportClick(
        {
          givenName: buildGivenName(relative.first_name, relative.middle_name),
          surName: relative?.last_name || '',
          birthDate: buildFSDateString(
            relative.dob_year,
            relative.dob_month,
            relative.tokenized_dob_day || null
          ),
          deathDate: buildFSDateString(
            relative.dod_year,
            relative.dod_month,
            relative.tokenized_dod_day || null
          ),
          familySearchId: id,
          city: relative.places[0]?.city || '',
          state: relative.places[0]?.state || '',
          country: relative.places[0]?.country || '',
        },
        setIsLoadingAncestor
      );
    });
  }, [createAncestorReportDispatch]);

  useEffect(() => {
    openPetModalDispatch.on('openPetModal', function (petId, memberId) {
      setPetEditModalOpen(true);
      setSelectedNode(memberId);
      setSelectedPet(petId);
    });
  }, [openPetModalDispatch]);

  useEffect(() => {
    handleBVReportOpenDispatch.on(
      'handleBVReportOpen',
      async ({ id, bvid }) => {
        try {
          trackEventGA4({
            event_name: 'family_tree_engagement',
            type: 'person report',
          });

          window.open(`report/person?bvid=${bvid}`, '_blank');

          const relative = relativesList.find(
            (relative) =>
              relative.id == id &&
              relative.relationship !== 'You' &&
              !relative.relatives_expanded
          );

          // Validate if the user is trying to run a report on themselves or a relative that has already been expanded
          if (!relative || !relative.bvid) return;

          setRelativeToRunReportOn(relative);

          const reportMeta = await reportGetters.person({
            queryParams: new URLSearchParams({
              bvid: relative.bvid,
              showPartialAddresses: isInPartialAddressesVariation.toString(),
            }),
          });

          handleReportMeta(reportMeta);
        } catch (error) {
          handleError(error as Error);
        }
      }
    );
  }, [handleBVReportOpenDispatch]);

  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data && !!config && isLoading) {
      const report = ReportFactory.create(queryResult?.data, 'person');

      const reportOwner = report.getOwner({
        potentialOwnerIndex: 0,
        showHighConfidenceDataOnly: true,
        higherConfidenceThreshold: config.higherConfidenceThreshold,
      });

      prepareAndPatchTeaserData({
        reportOwner,
        relativeToRunReportOn,
        patchMember,
        setIsLoading,
      });

      setRelativeToRunReportOn(undefined);
      setRelativeToRunPermalink('');
    }
  }, [queryResult.isSuccess, queryResult.data, config]);

  useEffect(() => {
    const props = {
      selection,
      onSelection,
      data,
      theme,
      imgFamilyTreePlaceholder,
      imgFamilyTreeAddRelative,
      imgFamilyTreeDeceased,
      imgFamilySearchIcon,
      imgFamilyTreePetIcon,
      imgFamilyTreeOriginalMember,
      handleNodeClick,
      softDeleteDispatch,
      patchMemberDispatch,
      patchMemberFromDrag,
      softDestroyMemberFromNode,
      addAdditionalSpouseDispatch,
      createAncestorReportDispatch,
      isLoadingAncestor,
      openPetModalDispatch,
      trackEventGA4,
      handleBVReportOpenDispatch,
    };

    if (prevProps) {
      const resetPosition =
        data !== prevProps.data || props.selection !== prevProps.selection;
      chartWrapper.current.renderChart(props, {
        initialRender: true,
        resetPosition,
      });
    } else {
      chartWrapper.current.renderChart(props, {
        initialRender: true,
        resetPosition: true,
      });
    }

    setLoaded(true);
  }, [setLoaded, data, selection]);

  useEffect(() => {
    if (loaded) {
      if (isPreview) {
        // Doing this to disable the onclick of the see report btn
        const nodes = selectAll('[data-bvid]');
        nodes.on('click', null);

        return;
      }

      // Select all the placeholders
      // and apply on drop & on click logic.
      const placeholders = selectAll('.placeholder');
      const nodes = placeholders.nodes() as HTMLElement[];

      if (!nodes.length) return;

      nodes.forEach((node) => {
        node.ondragover = (e) => {
          e.preventDefault();
        };

        node.ondrop = (e: DragEvent) => {
          const newIndiData = e.dataTransfer?.getData('application/json');
          if (!newIndiData) return;

          const parsedData = JSON.parse(newIndiData);
          if (!parsedData) return;

          const currentTargetElement = e.currentTarget as HTMLElement | null;
          if (!currentTargetElement) return;

          const { dataset } = e.currentTarget as HTMLElement;
          const { relationship, id } = dataset;

          if (!id) return;
          if (parsedData.pet) {
            parsedData.pet.member_id = id;
            postPetMutation(parsedData.pet);
            return;
          }

          if (!parsedData.indi) return;

          trackEventGA4({
            event_name: 'family_tree_engagement',
            type: 'drop relative',
          });
          parsedData.indi.relationship = relationship;
          parsedData.id = id;

          patchMemberFromDrag(parsedData);
        };

        node.onclick = (e) => {
          const currentTarget = e.currentTarget as HTMLElement;
          const { id, relationship } = currentTarget.dataset;

          if (!id || !relationship) return;

          handleNodeClick({
            id,
            relationship,
          });
        };
      });
    }
  }, [loaded, data, trackEventGA4, patchMemberFromDrag]);

  const handleError = (error: Error) => {
    logError('Error running report on relative', error);
    navigate('/error');
  };

  const handleReportMeta = (response: SearchReportResponseData) => {
    const permalink = response?.report?.permalink;

    if (permalink) {
      setIsLoading(true);
      setRelativeToRunPermalink(permalink);

      return;
    }

    throw new Error('No permalink found');
  };

  const Container = isPreview ? PreviewSvgContainer : SvgContainer;
  const ChartSvgContainer = isPreview ? PreviewChartSvg : ChartSvg;

  return (
    <>
      <EditRelationshipModal
        modalOpen={editRelationshipModalOpen}
        setModalOpen={setEditRelationshipModalOpen}
        selectedNode={selectedNode}
        setSelectedNode={setSelectedNode}
        patchMemberFromDrag={patchMemberFromDrag}
      />
      <EditPetModal
        modalOpen={petEditModalOpen}
        setModalOpen={setPetEditModalOpen}
        selectedNode={selectedNode}
        selectedPet={selectedPet}
        setSelectedNode={setSelectedNode}
        setSelectedPet={setSelectedPet}
        patchMemberFromDrag={patchMemberFromDrag}
      />
      <Container id="svgContainer">
        {!isPreview && (
          <StyledStack direction="column">
            <Tooltip title="Zoom-In" placement="right" arrow>
              <StyledIconButton
                color="primary"
                size="large"
                onClick={() => chartWrapper.current.zoom(ZOOM_FACTOR)}
              >
                <Icons.ZoomIn fontSize="inherit" />
              </StyledIconButton>
            </Tooltip>
            <Tooltip title="Zoom-Out" placement="right" arrow>
              <StyledIconButton
                color="primary"
                size="large"
                sx={{ borderTop: 'none' }}
                onClick={() => chartWrapper.current.zoom(1 / ZOOM_FACTOR)}
              >
                <Icons.ZoomOut fontSize="inherit" />
              </StyledIconButton>
            </Tooltip>
          </StyledStack>
        )}

        {!isLoading ? (
          <div>
            {!isPreview && <Tips sideMenuOpen={sideMenuOpen} />}
            <ChartSvgContainer id="chartSvg">
              <g id="chart" />
            </ChartSvgContainer>
          </div>
        ) : (
          <Stack
            mt={[3]}
            sx={{ justifyContent: 'center' }}
            data-testid="loading"
          >
            <LoadingDots count={5} />
          </Stack>
        )}
      </Container>
    </>
  );
}
